import React from "react";
import { AxiosResponse } from "axios";
import { OptionsType } from "react-select";
import { PdfOrientationType } from "go-core/types";
import { ClientGrantApi } from "../components/ClientDetails/services/types";

export interface CardApi {
	id: number;
	contact_id: number;
	card_number: string;
	card_code: string;
	card_expiry_date: string;
}

export interface ExportCSVTranslationsApi {
	export_csv?: string;
	resource_name: string;
	problems_column_name?: string;
	constraints: Record<string, string>;
	database_column_name: Record<string, string>;
}

export enum AvailabilityStatusApi {
	ENABLED = "ENABLED",
	DISABLED = "DISABLED",
	DELETED = "DELETED",
}

export interface AvailabilityApi {
	id: number;
	name: string;
	hours?: AvailabilityHourItem[];
	dates?: AvailabilityDateItem[];
	status: AvailabilityStatusApi;
}

export interface AvailabilityHourItem {
	id?: number;
	day_from: string;
	day_to: string;
	hour_from: string;
	hour_to: string;
}

export interface AvailabilityDateItem {
	id: number;
	date_range: string | null;
	date_from?: string;
	date_to?: string;
}

export interface AvailabilityRequests {
	updateAvailability: (
		availability: AvailabilityApi,
		params?: Record<string, any>,
		organizationId?: number
	) => Promise<AvailabilityApi>;
	createAvailability: (availability: AvailabilityApi, organizationId?: number) => Promise<AvailabilityApi>;
	removeAvailability: (id: number, organizationId?: number) => Promise<{}>;
	restoreAvailability: (id: number, organizationId?: number) => Promise<AvailabilityApi>;
	getAvailability: (id: number, params?: Record<string, any>, organizationId?: number) => Promise<AvailabilityApi>;
	getAvailabilities: (
		params?: Record<string, any>,
		options?: Record<string, any>,
		organizationId?: number
	) => Promise<AvailabilityApi[]>;
}

export interface AvailabilitiesProps {
	requests: AvailabilityRequests;
	organizationId?: number;
	organizationName?: string;
	companyName?: string;
	taxIdNo?: string;
	pdfOrientation?: PdfOrientationType;
	pdfFontSize?: string;
}

export interface AdminJobApi {
	id: string;
	name: string;
	organization_id: string;
	status: string;
	created_at: string;
	updated_at: string;
	error: string;
}

export interface DiagnoseApi {
	message: string;
	elements: DiagnoseElementApi[];
	fix_id?: string;
}

export interface DiagnoseElementApi {
	resource_type: string;
	id: string;
	link?: string;
}

export interface DiagnosticsRequests {
	getDiagnosticsTypes: (organizationId?: number) => Promise<string[]>;
	diagnose: (types: string[], organizationId?: number, options?: Record<string, any>) => Promise<string>;
	getDiagnoseProgress: (
		jobUid: string,
		organizationId?: number,
		options?: Record<string, any>
	) => Promise<AxiosResponse>;
	diagnoseFix: (fixId: string, organizationId?: number) => Promise<{}>;
}

export interface ImportOverviewFormSelects {
	id: string;
	label: string;
	options: OptionsType<Record<string, any>>;
	isRequired?: true;
}

export interface ImportPreviewItemsColumn {
	id: string;
	name: string;
	render?: (currentCellData: any) => React.ReactNode;
}

export interface DryImportMapping {
	id: string;
	hasSkipOption?: true;
}

export interface ImportOverviewConfig {
	formSelects?: ImportOverviewFormSelects[];
	dryImportProgressIncludes: string;
	previewItemsResponseKey: string;
	previewNestedItemsResponseKey: string;
	previewItemsColumns: ImportPreviewItemsColumn[];
	previewNestedItemsRender: (subItem: any) => React.ReactNode;
	dryImportMappings: DryImportMapping[];
	previewItemsKeyName: string;
}

export interface AllAvailableItemsApi {
	id: number;
	name: string;
}

interface CsvImportMappingValue {
	csv_value: string;
	db_id: string;
}

export type CsvImportMappings = { mappings: Record<string, CsvImportMappingValue[]> };
export type CsvImportOverviewFormType = CsvImportMappings & Record<string, any>;

export interface ImportOverviewProblemApi {
	count: number;
	message: string;
}

export interface ImportOverviewProblemsApi {
	errors?: ImportOverviewProblemApi[];
	warnings?: ImportOverviewProblemApi[];
}

interface MappedValueApi {
	id: number;
	name: string;
}

interface ImportCsvOverviewMappedItemApi {
	csv_value: string;
	mapped_value?: MappedValueApi | string;
}

interface MappedItemApi {
	mapped: ImportCsvOverviewMappedItemApi[];
	all_available_items: AllAvailableItemsApi[];
}

export type ImportCsvOverviewWrongDataApi = Record<string, MappedItemApi>;
export type ImportCsvOverviewApi = ImportOverviewProblemsApi & ImportCsvOverviewWrongDataApi;

export interface OrganizationClientGrantsProps {
	getClientGrants: (
		params?: Record<string, any>,
		options?: Record<string, any>
	) => Promise<OrganizationClientGrantApi[]>;
	getClientGrant: (clientGrantId: string, params?: Record<string, any>) => Promise<OrganizationClientGrantApi>;
	revokeClientGrant: (clientGrantId: number, params?: Record<string, any>) => Promise<OrganizationClientGrantApi>;
	createClientGrant: (
		clientGrantData: ClientGrantApi,
		params?: Record<string, any>
	) => Promise<CreateOrganizationClientGrantApi>;
	updateClientGrant: (
		clientGrantId: number,
		clientGrantData: OrganizationClientGrantApi,
		params?: Record<string, any>
	) => Promise<OrganizationClientGrantApi>;
	getDocsPath: () => Promise<{ api_documentation_url: string }>;
	organizationName?: string;
	organizationTimezone?: string;
	taxIdNo?: string;
	company?: string;
	pdfOrientation?: PdfOrientationType;
	pdfFontSize?: string;
}

export interface ClientGrantsCreateModalProps
	extends Pick<OrganizationClientGrantsProps, "createClientGrant" | "updateClientGrant"> {
	onHide: () => void;
	onRefresh: () => Promise<void>;
	defaultValues?: ClientGrantApi;
}

export interface OrganizationClientGrantApi extends ClientGrantApi {
	label: string;
}

export interface CreateOrganizationClientGrantApi extends OrganizationClientGrantApi {
	secret_id: string;
}
